import { FORM_ERROR } from 'final-form';
import { useTranslation } from 'next-i18next';
import { FC, useMemo, useRef } from 'react';
import { useFormState } from 'react-final-form';
import Typography from 'src/common/components/Typography';
import { TripCartFormState } from 'src/listing/types';
import TripCartDateInput from 'src/trip-cart-deprecated/components/TripCartDateInput';
import TripCartAsidePriceHeader from 'src/trip-cart-deprecated/components/aside/TripCartAsidePriceHeader';
import TripCartBookingButtons from 'src/trip-cart-deprecated/components/TripCartBookingButtons';
import TripCartCaptainAndMultiday from 'src/trip-cart-deprecated/components/TripCartCaptainAndMultiDay';
import TripCartDepartureTime from 'src/trip-cart-deprecated/components/TripCartDepartureTime';
import TripCartGuests from 'src/trip-cart-deprecated/components/TripCartGuests';
import TripCartMessage from 'src/trip-cart-deprecated/components/TripCartMessage';
import TripCartAsidePanel from 'src/trip-cart-deprecated/components/aside/TripCartAsidePanel';
import InquiryBanner from 'src/common/components/InquiryBanner';
import { useTripCartContext } from 'src/trip-cart-deprecated/hooks/useTripCartContext';
import useOverflowDetection from 'src/common/hooks/useOverflowDetection';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import BookingMotivator from 'src/common/components/BookingMotivator';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import { BOOKING_MOTIVATOR_STATES } from 'src/common/constants';
import { getDaysUntilInstabook } from 'src/common/utils/dateHelpers';
import SnoozeStatus from 'src/listing/components/SnoozeStatus';
import styles from './TripCartAsideRoot.module.scss';

const TripCartAsideRoot: FC = () => {
  const [t] = useTranslation('listing-page');
  const {
    submitErrors,
    values: {
      formSelectedDates,
    },
    dirtySinceLastSubmit,
  } = useFormState<TripCartFormState>();
  const {
    threadId, listing, instabookDates, snoozeActive,
  } = useTripCartContext();
  const ref = useRef<HTMLDivElement>(null);
  const scrollable = useOverflowDetection({ ref });
  const { getSelectedMotivators } = useBookingMotivatorsContext();
  const [start, end] = formSelectedDates;

  const motivators = useMemo(() => (
    getSelectedMotivators([start, end])
  ), [end, getSelectedMotivators, start]);

  const showMotivators = useMemo(
    () => !threadId
    && motivators.some((motivator) => motivator !== BOOKING_MOTIVATOR_STATES.LOOKS_GOOD),
    [motivators, threadId],
  );

  const daysUntilInstabook = getDaysUntilInstabook(formSelectedDates, instabookDates);

  return (
    <>
      <div className={styles.root}>
        {!snoozeActive && (
          <div className={styles.scroll} ref={ref}>
            <InquiryBanner variant="desktop" threadId={threadId} />
            {showMotivators && (
              <BookingMotivator
                variant="compact"
                motivators={motivators}
                anticipatedDays={listing.cancellation_anticipation}
                daysUntilInstabook={daysUntilInstabook}
                size="m"
                hardEdge
              />
            )}
            <div className={getClassNames(styles, 'header', classNames({ scrollable }))}>
              <TripCartAsidePriceHeader />
            </div>
            <div className={getClassNames(styles, 'content', classNames({ scrollable }))}>
              <TripCartCaptainAndMultiday />
              <TripCartDateInput />
              <div className={styles.row}>
                <TripCartDepartureTime />
                <TripCartGuests />
              </div>
              <TripCartMessage />
              {!dirtySinceLastSubmit
              && submitErrors?.[FORM_ERROR] && (
                <Typography type="p" size="body-m" className={styles.formError}>
                  {t(submitErrors?.[FORM_ERROR])}
                </Typography>
              )}
            </div>
            <div className={getClassNames(styles, 'footer', classNames({ scrollable }))}>
              <TripCartBookingButtons position="trip-cart-cost-panel" />
            </div>
          </div>
        )}
        <div className={styles.desktopSnooze}>
          <SnoozeStatus
            reason={listing.snooze_reason as string}
            endDate={listing.snooze_end_date as string}
            snoozeActive={snoozeActive}
          />
        </div>
      </div>
      <TripCartAsidePanel />
    </>
  );
};

export default TripCartAsideRoot;
